/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {Component} from 'vue-property-decorator';
import {FileInterface} from '@/store/modules/issues/types';
import {Action, Getter, State} from 'vuex-class';
import {FormStateInterface, IssueFormData, PCHardwareInterface} from '@/store/modules/form/types';
import IssueTitleInfo from '@/components/IssueTitleInfo/index.vue';
import {
    GameCategoryInterface,
    GameInterface,
    GamePlatformInterface,
    GamePlatformSubTypeInterface,
    GameVersionInterface,
} from '@/store/modules/games/types';
import {AxiosResponse} from 'axios';
import Loading from '@/components/Loading/index.vue';
import Scroller from '@/mixins/Scroller';
import {mixins} from 'vue-class-component';
import {LoadingState, LoadingStateInterface} from '@/components/Loading/types';
import Captcha from '@/mixins/Captcha';


/**
 * Report Stage 4.Finish component.
 *
 */
@Component({
    name: 'c-report-stage-finish',
    components: {
        'c-loading': Loading,
        'c-issue-title-info': IssueTitleInfo,
    },
})
export default class ReportStageFinish extends mixins(Scroller, Captcha) {

    @State('formStateModule') public formState!: FormStateInterface;
    @Action('updateStage', {namespace: 'formStateModule'}) public updateStage!: (stage: number) => void;

    @Action('fetchGames', {namespace: 'gamesStateModule'}) public fetchGames!: () => Promise<void>;
    @Getter('getGames', {namespace: 'gamesStateModule'}) public getGames!: GameInterface[];

    // Form Submit Report
    @Action('cancelReportIssue', {namespace: 'formStateModule'}) public cancelReportIssue!: () => void;
    @Action('submitReportIssue', {namespace: 'formStateModule'})
    public submitReportIssue!: (issueData: IssueFormData) => Promise<number>;
    @Action('submitReportIssueDetails', {namespace: 'formStateModule'})
    public submitReportIssueDetails!: (issueDetailsData: any) => number;
    @Action('submitReportPC', {namespace: 'formStateModule'}) public submitReportPC!: (formData: any) => AxiosResponse;
    @Action('submitReportPS4', {namespace: 'formStateModule'})
    public submitReportPS4!: (formData: any) => AxiosResponse;
    @Action('submitReportPlatformGeneric', {namespace: 'formStateModule'})
    public submitReportPlatformGeneric!: (formData: any) => AxiosResponse;
    @Action('submitReportXBOX', {namespace: 'formStateModule'})
    public submitReportXBOX!: (formData: any) => AxiosResponse;
    @Action('submitReportSwitch', {namespace: 'formStateModule'})
    public submitReportSwitch!: (formData: any) => AxiosResponse;
    @Action('uploadToS3', {namespace: 'issuesStateModule'}) public uploadToS3: any;
    @Action('createEvidence', {namespace: 'issuesStateModule'}) public createEvidence: any;
    @Action('createMedia', {namespace: 'issuesStateModule'}) public createMedia: any;

    @Action('subscribe', {namespace: 'formStateModule'}) public subscribe: any;

    public game: GameInterface | null = null;
    public platform: GamePlatformInterface | null = null;
    public platformType: GamePlatformSubTypeInterface| null = null;
    public category: GameCategoryInterface | null = null;
    public version: GameVersionInterface | null = null;
    public state: LoadingStateInterface = new LoadingState(false, false);

    /**
     * Mounted life-cycle event.
     *
     * Initialises the loader.
     */
    public async mounted(): Promise<void> {
        // add ?step=4 to the url so browser back button can go back to step 3
        history.pushState({page: 4}, 'Finish', '?step=4');

        window.onpopstate = (
            (event: PopStateEvent): void => {
                if (event.state) {
                    const nextPage: number = event.state.page;
                    if (nextPage === this.formState.stage - 1) {
                        this.previous();
                    }
                }
            });

        try {
            this.state.start();
            await this.fetchGames();

            this.game = this.getGames.find((game: GameInterface): boolean => {
                return this.formState.reportStage.game === game.id;
            }) as GameInterface;

            this.platform = this.game.platforms.find((platform: GamePlatformInterface): boolean => {
                return platform.id === this.formState.reportStage.platform;
            }) as GamePlatformInterface;

            this.platformType = this.platform.types.find((type: GamePlatformSubTypeInterface) => {
                return this.formState.detailsStage.hardware.type === type.id;
            }) as GamePlatformSubTypeInterface;

            this.category = this.game.categories.find((cat: GameCategoryInterface) => {
                return this.formState.reportStage.category === cat.id;
            }) as GameCategoryInterface;

            this.version = this.game.versions.find((version: GameVersionInterface) => {
                return this.formState.reportStage.version === version.id;
            }) as GameVersionInterface;
            this.reloadRecaptcha();
            this.state.complete();
        } catch (e) {
            this.state.failed(e.message);
        } finally {
            this.scrollTop();
        }

    }

    /**
     *  Progress to the next step if form values are valid.
     *
     */
    public previous(): void {
        this.updateStage(this.formState.stage - 1);
    }

    /**
     * Recaptcha verification.
     * @param {string} token
     * @return {Promise<void>}
     */
    public async onVerify(token: string): Promise<void> {
        this.captchaResponse = token;
        this.state.start();

        // Construct issue data
        const issueData: IssueFormData = {
            title: this.formState.reportStage.problemTitle as string,
            browser_locale: navigator.language,
            category: `api/categories/${(this.category as GameCategoryInterface).id}`,
            captcha_token: token,
        };

        if (this.version !== undefined) {
            issueData['version'] = `api/versions/${(this.version as GameVersionInterface).id}`;
        }

        try {
            // Submit Issue
            const issueId: number = await this.submitReportIssue(issueData);
            const platformId: string | undefined = await this.constructDataAndSubmitPlatform();
            const issueDetailsId: number = await this.constructDataAndSubmitIssueDetails(issueId, platformId);

            if (this.formState.detailsStage.subscription) {
                await this.subscribe(issueId);
            }

            if (!!this.formState.detailsStage.hardware.mediaFiles) {
                await this.uploadAndLinkImages(issueDetailsId, this.formState.detailsStage.hardware.mediaFiles);
            }

            this.cancelReportIssue();
            this.state.complete();
            this.$router.push({name: 'issue-detail', params: {id: JSON.stringify(issueId)}});

            this.$toastr.success(this.$i18n.translate('component.reportIssue.success', {}) || '');
        } catch (e) {
            this.$toastr.error(this.$i18n.translate('component.reportIssue.error.general', {}) || '');
            this.state.failed(e.message);
        }
    }

    /**
     * Upload the images and link to issue detail.
     * @param issueDetailsId
     * @param {File[]} images
     * @return {Promise<void>}
     */
    public async uploadAndLinkImages(
        issueDetailsId: number,
        images: File[],
    ): Promise<void> {
        const evidenceIds: number[] = [];

        for (const file of images) {
            // get pre-signed urls for images
            const type: string = file.type ? file.type : 'text/plain';

            const fileItem: FileInterface = await this.createMedia({
                name: file.name,
                mime_type: type,
                size: file.size,
                path: `/issue_details/${issueDetailsId}`,
            });

            // upload the images
            try {
                await this.uploadToS3({preSignedUrl: fileItem.presignedUrl, file});
                evidenceIds.push(fileItem.id);
            } catch (e) {
                // ignore exception and continue
            }
        }

        // link the images
        if (typeof evidenceIds !== 'undefined' && evidenceIds.length > 0) {
            await this.createEvidence({
                fileIdsData: evidenceIds,
                issueDetailsId,
            });
        }
    }

    /**
     *  Progress to the next step if form values are valid.
     * @return {Promise<void>}
     */
    public async validateAndSubmit(): Promise<void> {
        await this.submit();
    }

    /**
     * @param {number} issueId
     * @param {string} platformId
     * @return {Promise<any>}
     */
    private async constructDataAndSubmitIssueDetails(issueId: number, platformId?: string): Promise<any> {
        const issueDetailsData: any = {
            description: this.formState.detailsStage.problem.problemDescription,
            can_reproduce: true,
            how_to_reproduce: this.formState.detailsStage.problem.reproductionSteps,
            time_seen: this.formState.detailsStage.problem.timesSeen,
            creator_type: 'creator',
            issue: '/api/issues/' + issueId,
            platform: platformId,
        };

        return this.submitReportIssueDetails(issueDetailsData);
    }

    /**
     * @return {Promise<string | undefined>}
     */
    private async constructDataAndSubmitPlatform(): Promise<string | undefined> {
        // Construct Platform Data
        const platformData: any = {
            region: this.formState.detailsStage.hardware.region,
            internet_provider: this.formState.detailsStage.hardware.internetProvider,
            internet_speed: this.formState.detailsStage.hardware.internetSpeed,
            additional_technical_info: this.formState.detailsStage.hardware.additionalTechnicalInfo,
            type: this.formState.detailsStage.hardware.type,
        };

        if (this.platform && (this.platform.slug === 'pc' || this.platform.slug === 'mac')) {
            this.$set(platformData, 'os',
                (this.formState.detailsStage.hardware as PCHardwareInterface).os);
            this.$set(platformData, 'processor',
                (this.formState.detailsStage.hardware as PCHardwareInterface).processor);
            this.$set(platformData, 'memory',
                (this.formState.detailsStage.hardware as PCHardwareInterface).systemMemory);
            this.$set(platformData, 'graphic_card_chip_type',
                (this.formState.detailsStage.hardware as PCHardwareInterface).graphicsCard);

            if (this.platform.slug !== 'mac') {
                this.$set(platformData, 'direct_x_version',
                    (this.formState.detailsStage.hardware as PCHardwareInterface).directVersion);
            }

            this.$set(platformData, 'loaded_via',
                (this.formState.detailsStage.hardware as PCHardwareInterface).loadedVia);
        }

        let response: AxiosResponse;

        // Submit Platform based on type
        try {
            switch ((this.platform as GamePlatformInterface).slug) {
                case 'pc' :
                    response = await this.submitReportPC(platformData);
                    return response.data['@id'];
                case 'playstation-4' :
                    response = await this.submitReportPS4(platformData);
                    return response.data['@id'];
                case 'xbox-one' :
                    response = await this.submitReportXBOX(platformData);
                    return response.data['@id'];
                case 'nintendo-switch' :
                    platformData.type = 'nsw';
                    response = await this.submitReportPlatformGeneric(platformData);
                    return response.data['@id'];
                case 'playstation-5' :
                    platformData.type = 'ps5';
                    response = await this.submitReportPlatformGeneric(platformData);
                    return response.data['@id'];
                case 'xbox-series-x' :
                    platformData.type = 'xbx';
                    response = await this.submitReportPlatformGeneric(platformData);
                    return response.data['@id'];
                case 'xbox-series-s' :
                    platformData.type = 'xbs';
                    response = await this.submitReportPlatformGeneric(platformData);
                    return response.data['@id'];
                case 'mac' :
                    platformData.type = 'mac';
                    response = await this.submitReportPlatformGeneric(platformData);
                    return response.data['@id'];
            }
        } catch (e) {
            return;
        }
    }

}
